// ensure error capture
import * as ErrorsService from "../../service-errors/service-errors"

import {
  Component,
  ComponentInterface,
  Element,
  forceUpdate,
  h,
  Listen,
  Prop,
  readTask,
  setMode,
  writeTask,
} from "@stencil/core"
import { FP, FPRx, RemoteData, Rx, RxOperators } from "../../deps"

import * as Events from "../../events/events"
import * as AnalyticsService from "../../service-analytics/service-analytics"
import * as FlagsService from "../../service-flags/service-flags"
import * as SellalongService from "../../service-sellalong/service-sellalong"
import * as ShopifyService from "../../service-shopify/service-shopify"
import * as ServiceIdentity from "../../service-identity/service-identity"

@Component({
  tag: "sellalong-provider",
  styleUrls: {
    theme_8629350: "./sellalong-provider-theme-sirplus.css",
    theme_27219324: "./sellalong-provider-theme-bmo.css",
  },
})
export class Provider implements ComponentInterface {
  /**
   * Shopify ID of the shop or null
   */
  @Prop() shopifyShopId: string | null = null

  /**
   * Shopify ID of the customer or null if not logged in yet
   */
  @Prop() shopifyCustomerId: string | null = null

  /**
   * Override theme for all components - defaults to shop id
   */
  @Prop() theme: string | null = null

  /**
   * Email address for the customer or null if using shopify-customer-id or unknown
   */
  @Prop() customerEmail: string | null = null

  /**
   * CSS selector for CTA
   */
  @Prop() ctaTargetSelector: string | null = null

  /**
   * CSS selector for panel
   */
  @Prop() panelTargetSelector: string | null = null

  /**
   * CSS selector for discount display / summary
   */
  @Prop() discountDisplayTargetSelector: string | null = null

  /**
   * CSS selector for checkout button
   */
  @Prop() checkoutCtaSelector: string | null = null

  /**
   * CSS selector for panel (mini variant)
   */
  @Prop() miniPanelTargetSelector: string | null = null

  /**
   * Attach sellalong help modal to body automatically
   */
  @Prop()
  renderHelpModal: boolean = true

  /**
   * Local flag to enable application of the discount code to a checkout
   */
  @Prop() flagEnableShopifyDiscountApply: boolean = true

  /**
   * Total number of items in the cart
   */
  @Prop()
  cartItemCount: number = 0

  /**
   * Total price of all items in the cart
   */
  @Prop()
  cartTotalPrice: number = 0

  /**
   * Automatic discounts applied to the cart. Sellalong's discounts are currently incompatible, so will be disabled.
   */
  @Prop()
  cartTotalDiscount: number = 0

  @Element()
  element!: HTMLElement

  // internal fields
  shopId: FP.option.Option<ShopifyService.ShopId> = FP.option.none
  enabled = false

  analyticsListener: Rx.Subscription | null = null
  checkoutListeners: ReadonlyArray<
    readonly [element: Element, listener: EventListener]
  > = []
  componentRemovalListener: Rx.Subscription | null = null
  loginStartListener: Rx.Subscription | null = null

  ctaElement: HTMLSellalongCtaElement | null = null
  panelElement: HTMLSellalongPanelElement | null = null
  miniPanelElement: HTMLSellalongPanelElement | null = null
  discountDisplayElement: HTMLSellalongDiscountDisplayElement | null = null
  helpModalElement: HTMLSellalongHelpModalElement | null = null

  @Listen("sellalongPurchaseSelectIntent", { target: "document" })
  purchaseSelectHandler(
    event: CustomEvent<Events.PurchaseStateTransitionIntent>
  ) {
    Events.publish(event.detail)
  }

  loadComponents = () => {
    this.attachEventListeners()

    if (!this.enabled) {
      return
    }

    // Detect when any sellalong component is removed
    // - likely when an ajax write has occurred in cart, replacing cart dom elements
    // - occurs when cart item quantity changes, or item added to cart
    if (this.componentRemovalListener === null) {
      this.componentRemovalListener = FP.function
        .pipe(
          Events.eventBus,
          RxOperators.filter(
            Events.isEvent("sellalongComponentRemovedInternal")
          ),
          RxOperators.throttleTime(500),
          RxOperators.tap(this.loadComponents)
        )
        .subscribe()
    }

    if (this.loginStartListener === null) {
      this.loginStartListener = FP.function
        .pipe(
          Events.eventBus,
          RxOperators.filter(Events.isEvent("loginStartIntent")),
          FPRx.observable.chain(
            FP.function.flow(this.identifyCustomer, FPRx.observable.fromTask)
          ),
          RxOperators.map(
            FP.either.fold(
              (error) => console.error("Provider login error", error),
              ({ email }) =>
                email && window.sessionStorage.setItem("sa_email", email)
            )
          )
        )
        .subscribe()
    }

    // schedule dom read task to avoid dom thrashing
    readTask(() => {
      try {
        this.miniPanelElement = Provider.injectComponent(
          "sellalong-panel",
          this.miniPanelTargetSelector,
          this.miniPanelElement,
          this.element,
          "after",
          {
            variant: "mini",
          }
        )

        this.ctaElement = Provider.injectComponent(
          "sellalong-cta",
          this.ctaTargetSelector,
          this.ctaElement,
          this.element,
          "prepend"
        )

        this.panelElement = Provider.injectComponent(
          "sellalong-panel",
          this.panelTargetSelector,
          this.panelElement,
          this.element,
          "append"
        )

        this.discountDisplayElement = Provider.injectComponent(
          "sellalong-discount-display",
          this.discountDisplayTargetSelector,
          this.discountDisplayElement,
          this.element,
          "replaceWith"
        )

        if (this.renderHelpModal) {
          this.helpModalElement = Provider.injectComponent(
            "sellalong-help-modal",
            "body",
            this.helpModalElement,
            this.element,
            "append"
          )
        }
      } catch (error: unknown) {
        ErrorsService.recordError(error)
        console.error(
          `sellalong-provider error: ${
            error instanceof Error ? error.message : String(error)
          }`
        )
      }
    })
  }

  attachEventListeners() {
    readTask(() => {
      if (this.checkoutCtaSelector === null) {
        return
      }

      // remove existing listeners
      FP.function.pipe(
        this.checkoutListeners,
        FP.readonlyArray.map(([elem, listener]) =>
          elem.removeEventListener("click", listener)
        )
      )

      // attach checkout listeners
      this.checkoutListeners = FP.function.pipe(
        this.element.ownerDocument.querySelectorAll(this.checkoutCtaSelector),
        Array.from as <A>(a: ArrayLike<A>) => A[],
        FP.readonlyArray.fromArray,
        FP.readonlyArray.map((elem) => {
          const listener: EventListener = Events.createPublishCallback({
            kind: "checkoutIntent",
            originSection: "cart",
          })
          elem.addEventListener("click", listener)
          return [elem, listener] as const
        })
      )
    })
  }

  connectedCallback() {
    // ensure cart total is set before `identifyCustomer()`
    SellalongService.setCartTotal(this.cartTotalPrice)
    SellalongService.setCartItemCount(this.cartItemCount)

    this.shopId = FP.function.pipe(this.shopifyShopId, ShopifyService.toShopId)

    if (this.analyticsListener === null && FP.option.isSome(this.shopId)) {
      this.analyticsListener = AnalyticsService.init(this.shopId.value)
    }

    // set theme via stencil's `setMode()`
    FP.function.pipe(
      this.theme,
      ShopifyService.toShopId,
      FP.option.alt(() => this.shopId),
      FP.option.map((themeId) => {
        setMode(() => `theme_${themeId}`)
      })
    )
  }

  // loads customer data
  identifyCustomer = <A extends { email: string | null }>({ email }: A) =>
    FP.function.pipe(
      this.shopId,
      FP.taskEither.fromOption(
        () => new Error(`Shop ID not found: ${this.shopifyShopId}`)
      ),
      FP.taskEither.map((shopId) => ({
        shopifyShopId: shopId,
        shopifyCustomerId: this.shopifyCustomerId,
        email,
      })),

      FP.taskEither.bind("identity", SellalongService.identifyCustomer)
    )

  awaitSidebarCartVisible: () => FP.taskEither.TaskEither<Error, boolean> =
    FP.function.flow(
      () => this.miniPanelTargetSelector,
      FP.option.fromNullable,
      FP.option.traverse(FP.io.Applicative)(
        (_selector) => () =>
          FP.function.pipe(
            document.getElementById("sidebar-cart"), // FIXME generalise to not be Sirplus specific
            FP.either.fromNullable(
              new Error("Failed to find sidebar cart element")
            )
          )
      ),
      FP.task.fromIO,
      FP.task.chain(
        FP.option.fold(
          // No mini-panel, so always load
          FP.function.flow(FP.function.constFalse, FP.taskEither.of),

          // Wait for mini-panel selector to be viewed before loading
          FP.function.flow(
            FP.either.filterOrElse(
              () => !!window.IntersectionObserver,
              () => new Error("Intersection observer not supported")
            ),
            FP.taskEither.fromEither,
            FP.taskEither.chain(
              (elem): FP.taskEither.TaskEither<Error, boolean> =>
                () =>
                  FP.function.pipe(
                    new Rx.Observable<IntersectionObserverEntry[]>(
                      (subscriber) => {
                        const observer = new IntersectionObserver(
                          (entries) => subscriber.next(entries),
                          {
                            root: null,
                            threshold: 0.5,
                          }
                        )

                        observer.observe(elem)

                        return () => observer.disconnect()
                      }
                    ),
                    RxOperators.map(
                      FP.readonlyArray.findFirst(
                        (entry) => entry.isIntersecting
                      )
                    ),
                    FPRx.observable.filterMap(
                      FP.option.map(
                        FP.function.flow(FP.function.constTrue, FP.either.of)
                      )
                    ),
                    Rx.firstValueFrom as <T>(
                      source: Rx.Observable<T>
                    ) => Promise<T>
                  )
            )
          )
        )
      )
    )

  identifyUserForErrorsService: () => FP.taskEither.TaskEither<Error, void> =
    FP.function.flow(
      () => ServiceIdentity.getAnonId,
      FP.io.chain((anonId) => () => ErrorsService.setUser(anonId)),
      FP.taskEither.fromIO as <A>(
        a: FP.io.IO<A>
      ) => FP.taskEither.TaskEither<Error, A>
    )

  cartImpression: <
    A extends {
      sidebarCart: boolean
    }
  >(
    input: A
  ) => FP.taskEither.TaskEither<Error, void> = ({ sidebarCart }) =>
    FP.function.pipe(
      Events.createPublishCallback({
        kind: "cartImpressionAction",
        fullPage: !sidebarCart,
      }),
      FP.taskEither.fromIO as <A>(
        a: FP.io.IO<A>
      ) => FP.taskEither.TaskEither<Error, A>
    )

  initFlagsService: <
    A extends {
      customer: Pick<FlagsService.UserParameters, "shopifyShopId" | "identity">
    }
  >(
    input: A
  ) => FP.taskEither.TaskEither<Error, unknown> = FP.function.flow(
    ({ customer: { shopifyShopId, identity } }) => ({
      shopifyShopId,
      isInviteLinkReferral: SellalongService.isInviteLinkReferral(),
      identity,
    }),
    FP.taskEither.of,
    FP.taskEither.bind("viewerLocation", SellalongService.getViewerCountry),
    FP.taskEither.bindW(
      "hasSeen",
      FP.function.flow(
        () => localStorage && localStorage.getItem("sa_seen") === "true",
        FP.taskEither.of
      )
    ),
    FP.taskEither.chain(FlagsService.identifyUser)
  )

  setCartCorrelationId = FP.function.flow(
    () => ServiceIdentity.getAnonId,
    FP.io.map((id) => ({
      attributes: {
        sellalong_correlation_id: id,
      },
    })),
    FP.taskEither.fromIO,
    FP.taskEither.chain(ShopifyService.updateCart)
  )

  componentWillLoad = FP.function.flow(
    (): FP.taskEither.TaskEither<Error, { email: string | null }> =>
      FP.taskEither.of({ email: this.customerEmail }),

    FP.taskEither.chainFirst(this.identifyUserForErrorsService),

    // waits for mini panel to be visible before loading customer data and components
    FP.taskEither.bind("sidebarCart", this.awaitSidebarCartVisible),

    FP.taskEither.bind("customer", this.identifyCustomer),

    FP.taskEither.chainFirst(this.initFlagsService),

    FP.taskEither.bind(
      "enabled",
      FP.function.flow(
        () => FlagsService.variation("global.enabled", false),
        FP.task.chainFirstIOK((enabled) => () => {
          this.enabled = enabled
          AnalyticsService.setUiEnabled(enabled)
          if (enabled && localStorage) {
            localStorage.setItem("sa_seen", "true")
          }
        }),
        FP.taskEither.fromTask as <A>(
          a: FP.task.Task<A>
        ) => FP.taskEither.TaskEither<Error, A>
      )
    ),

    FP.taskEither.chainFirst(
      FP.function.flow(
        // Fire cartImpressionAction on load
        this.cartImpression,

        // Set correlation id on all shopping carts for analytics impression -> order correlation
        FP.taskEither.chain(this.setCartCorrelationId)
      )
    ),

    FP.task.map(
      FP.either.fold(
        (error) => {
          ErrorsService.recordError(error)
          console.error(`Error initialising Sellalong: ${error.message}`)
        },

        this.loadComponents
      )
    ),

    FP.function.apply(FP.function.constVoid)
  )

  render() {
    const discountAmount = SellalongService.getSellalongDiscountAmount()
    const maxCreditAvailable = SellalongService.getMaxSellbackAmount()
    const cartItemCount = SellalongService.getCartItemCount()
    const cartTotal = SellalongService.getCartTotal()
    const cartTotalAfterDiscount = SellalongService.getCartTotalAfterDiscount()
    const customerData = SellalongService.getCustomerData()
    const hasCustomer = SellalongService.hasCustomer()
    const rolledOverCredit =
      SellalongService.getSellalongDiscountRolledoverTotal()

    if (this.ctaElement) {
      this.ctaElement.discountAmount = FP.option.toNullable(discountAmount)
      this.ctaElement.maxCreditAvailable = maxCreditAvailable
    }

    if (this.discountDisplayElement) {
      this.discountDisplayElement.discountAmount =
        FP.option.toNullable(discountAmount)
      this.discountDisplayElement.cartTotal = cartTotal
      this.discountDisplayElement.cartTotalAfterDiscount =
        cartTotalAfterDiscount
    }

    if (this.panelElement) {
      this.panelElement.purchases = FP.function.pipe(
        customerData,
        RemoteData.map((data) => data.purchases)
      )
      this.panelElement.showLoggedOut = !hasCustomer
      this.panelElement.discountAmount = FP.option.toNullable(discountAmount)
      this.panelElement.cartItemCount = cartItemCount
      this.panelElement.cartTotal = cartTotal
      this.panelElement.cartTotalAfterDiscount = cartTotalAfterDiscount
      this.panelElement.rolledOverCredit =
        FP.option.toNullable(rolledOverCredit)
    }

    if (this.miniPanelElement) {
      this.miniPanelElement.purchases = FP.function.pipe(
        customerData,
        RemoteData.map((data) => data.purchases)
      )
      this.miniPanelElement.showLoggedOut = !hasCustomer
      this.miniPanelElement.discountAmount =
        FP.option.toNullable(discountAmount)
      this.miniPanelElement.cartItemCount = cartItemCount
      this.miniPanelElement.cartTotal = cartTotal
      this.miniPanelElement.cartTotalAfterDiscount = cartTotalAfterDiscount
      this.miniPanelElement.rolledOverCredit =
        FP.option.toNullable(rolledOverCredit)
    }

    return <slot />
  }

  static injectComponent<K extends keyof HTMLElementTagNameMap>(
    name: K,
    targetSelector: string | null,
    existingElement: HTMLElementTagNameMap[K] | null,
    parentElement: HTMLElement,
    method: "replaceWith" | "prepend" | "append" | "after",
    attributes: Record<string, any> = {}
  ): HTMLElementTagNameMap[K] | null {
    if (!targetSelector) {
      return null
    }

    // Only attempt to inject `existingElement` if its no longer attached to dom
    if (existingElement !== null && existingElement.isConnected) {
      return existingElement
    }

    const document = parentElement.ownerDocument
    const targetElement =
      targetSelector === "body"
        ? document.body
        : document.body.querySelector(targetSelector)

    if (!targetElement) {
      throw new Error(`Could not find target ${targetSelector}`)
    }

    const child =
      existingElement === null ? document.createElement(name) : existingElement

    FP.function.pipe(
      attributes,
      FP.record.mapWithIndex((name, value) => child.setAttribute(name, value))
    )

    const existing = targetElement.querySelector(name)

    // schedule dom write task to avoid dom thrashing
    writeTask(() => {
      if (existing && child !== existing) {
        existing.replaceWith(child)
      } else if (method === "replaceWith") {
        targetElement.replaceWith(child)
      } else if (method === "prepend") {
        targetElement.prepend(child)
      } else if (method === "after") {
        targetElement.after(child)
      } else {
        targetElement.append(child)
      }
      forceUpdate(parentElement)
    })
    return child
  }
}
